import DOMPurify from 'isomorphic-dompurify';

/**
 * Sanitizes input string to prevent XSS and enforces maximum length.
 * @param {string} input - The input string to sanitize.
 * @param {number} maxLength - Maximum allowed length (default 255).
 * @returns {string} - The sanitized string.
 * @throws {Error} - If input exceeds maximum length.
 */
export const sanitizeInput = (input, maxLength = 255) => {
    if (!input) return '';

    // Convert to string just in case
    const stringInput = String(input);
    const trimmed = stringInput.trim();

    if (trimmed.length > maxLength) {
        throw new Error(`Input exceeds maximum length of ${maxLength} characters`);
    }

    return DOMPurify.sanitize(trimmed, { ALLOWED_TAGS: [] });
};

/**
 * Returns the URL only if it parses cleanly AND uses http: or https:.
 * Anything else (javascript:, data:, mailto:, vbscript:, malformed) returns
 * null so callers can omit/disable the link.
 *
 * Defense-in-depth: backend schema validators (schemas/utility.py:url_scheme,
 * etc.) reject non-http(s) URLs at create/update time, but legacy rows or
 * out-of-band inserts can still slip through, and binding raw user input to
 * <a href={...}> would execute a `javascript:` payload on click. Use this any
 * time you render an `href` from data the user can influence.
 *
 * @param {string|null|undefined} url
 * @returns {string|null}
 */
export const isSafeHttpUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    try {
        const parsed = new URL(url);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? url : null;
    } catch {
        return null;
    }
};
